import { CountryData } from '@/pages/about';
import { ReactNode, createContext, useContext, useState } from 'react';

export type Tooltip = {
  data: CountryData;
  root: Element;
};

type GetIdFunction = (data: CountryData) => CountryIsoCode;

type TooltipState = 'CLOSED' | 'PREVIEW' | 'OPEN';

type TooltipContextProps = {
  getId: GetIdFunction;
  tooltip: Tooltip | null;
  tooltipState: TooltipState;
  isPointActive: (id: CountryIsoCode) => boolean;
  showTooltip: (tooltip: Tooltip) => void;
  openTooltip: (tooltip: Tooltip) => void;
  hideTooltip: () => void;
  closeTooltip: () => void;
};

const TooltipContext = createContext<TooltipContextProps>({
  getId: (data: CountryData) => data.id,
  tooltip: null,
  tooltipState: 'CLOSED',
  isPointActive: () => false,
  showTooltip: () => undefined,
  openTooltip: () => undefined,
  hideTooltip: () => undefined,
  closeTooltip: () => undefined,
});

export const useTooltipContext = () => useContext(TooltipContext);

type TooltipProviderProps = {
  children: ReactNode;
  getId: GetIdFunction;
};

export function TooltipProvider({ children, getId }: TooltipProviderProps) {
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const [tooltipState, setTooltipState] = useState<TooltipState>('CLOSED');

  function isPointActive(id: CountryIsoCode) {
    return tooltip?.data.id === id;
  }

  function showTooltip(tooltip: Tooltip) {
    if (tooltipState === 'OPEN') return;
    setTooltip(tooltip);
    setTooltipState('PREVIEW');
  }

  function hideTooltip() {
    if (tooltipState === 'OPEN') return;
    setTooltip(null);
    setTooltipState('CLOSED');
  }

  function openTooltip(tooltip: Tooltip) {
    setTooltip(tooltip);
    setTooltipState('OPEN');
  }

  function closeTooltip() {
    setTooltip(null);
    setTooltipState('CLOSED');
  }

  return (
    <TooltipContext.Provider
      value={{
        getId,
        tooltip,
        tooltipState,
        isPointActive,
        showTooltip,
        openTooltip,
        hideTooltip,
        closeTooltip,
      }}
    >
      {children}
    </TooltipContext.Provider>
  );
}
